import React from 'react'
import Grid from '@material-ui/core/Grid';
import styles from './CallForPapers.module.css'

const CallForPapers2k25 = () => {
	return (
    <React.Fragment>
			<div className={styles.root}>
				<h2>
					5th EDITION THEME – Topophilia: Geographies of Cinema
				</h2>
				<Grid container xs={12} className={`${styles.container} ${styles.column}`}>
					<p className={styles.paragraph}>
					Topophilia, a term popularised by human geographer Yi-Fu Tuan (1974), describes the affective bonds
					that individuals form with places — the emotional textures of environments, landscapes, memory, and attachment.
					</p>
					<p className={styles.paragraph}>
					When translated into cinema, topophilia provides a framework to inquire into how films imagine home, belonging,
					displacement, and rootedness. It asks us to consider what it means to love a place, be it lost, fictional, or
					contested. Secondly, what forms of attachment emerge in the relationship between cinematic landscapes and their
					spectators, and how does our interaction with the medium morph our bonds with spaces? In film, spaces become a
					site of feeling and imagination rather than simply a visual setting; human-ness itself is found in various
					geographies of scale.
					</p>
					<p className={styles.paragraph}>
					Building on Tuan's foundational premise, the idea that space is constructed, lived, and felt resonates strongly
					with Gaston Bachelard's (1964) notion of the home as a phenomenological shelter of memory and intimacy, and
					Henri Lefebvre's (1991) assertion that space is socially produced. Doreen Massey (2005) similarly conceptualises
					places as intersections of relations, always in motion, never fixed or stable. In cinema, this motion becomes
					aesthetic: through temporal layering, fragmentation, or sensory immersion. Films transform geography into the
					experience of memory, rupture, or belonging. Scott MacDonald's <b>The Garden in the Machine</b> (2001) further
					expands this approach by demonstrating how independent and avant-garde cinemas reshape our perception of place
					and ecology, positioning film itself as a mode of environmental awareness — a medium that cultivates new ways
					of relating to the spaces we inhabit and imagine.
					</p>
					<p className={styles.paragraph}>
					Post-colonial discourses trace migration, exile, and diaspora that complicate the idea of home and belonging,
					and its foundations in material reality. Hamid Naficy (2001) argues that exilic and accented cinemas construct
					hybrid spatial identities where home becomes a shifting constellation of affect, recollection, and longing.
					Chantal Akerman's <b>News from Home</b> (1976) exemplifies this: letters from afar overlay impersonal images of
					New York, producing a poetics of distance in which home survives only through voice and memory. Abbas
					Kiarostami's portrait of rural Iran in his <b>Koker Trilogy</b> or M.S Sathyu's visions of Indian partition in
					<b> Garam Hava</b> (1974) embody how Parallel Cinema movements are anchored in local landscapes and community
					narratives, asserting spatial specificity as a radical aesthetic and political assertion.
					</p>
					<p className={styles.paragraph}>
					Topophilia also demands that we examine cinema's ability to mythologise and instrumentalise place, particularly
					in relation to nationalism, war, and propaganda. Cultural geographer Denis Cosgrove (1984) frames landscapes as
					symbolic systems encoded with narratives of power, labour, and identity. Films such as <b>The Battle of Algiers</b> (1966)
					or <b>Soy Cuba</b> (1964) demonstrate how cinematic topography becomes a battleground for liberation and belonging,
					where home is continuously negotiated rather than inherited. Similarly, speculative genres extend topophilia into
					imaginative geographies, cyberfiction and fantasy construct new spatial orders, revealing cultural anxieties and
					utopian aspirations (Kitchin, 2013).
					</p>
					<p className={styles.paragraph}>
					Across national, documentary, experimental, militant, and speculative cinemas, space emerges not as background
					but as an active character shaping human experience. Films such as <b>Hiroshima mon amour</b> (1959), where a city
					becomes a palimpsest of trauma and intimacy; <b>The Florida Project</b> (2017), which juxtaposes the impermanency
					of a motel with "home"; or <b>Uncle Boonmee Who Can Recall His Past Lives</b> (2010), where forests become portals
					to memory and the supernatural, demonstrate the multiplicity of cinematic geographies and their emotional force.
					</p>
					<p className={styles.paragraph}>
					This call for papers invites scholarship exploring how cinema constructs, negotiates, and politicises the spaces
					we inhabit — including homeland and exile, the domestic and the public, real and speculative geographies. We
					welcome analyses that examine cinema through topophilia and spatial affect, viewing the filmmaker as
					cartographer, and reconsidering how films organise our sense of place as inseparable from love, home, and
					the self.
					</p>
					<p className={styles.paragraph}>
						<span>
							Themes which can be explored under 'Topophilia: Geographies of Cinema' include, but are not limited to:
						</span>
						<ul className={styles.listmain}>
							<li>Home, Love &amp; Affective Attachments</li>
							<li>Rurality, Folk-Land &amp; Homeland (Heimat)</li>
							<li>Gendered &amp; Queered Spaces</li>
							<li>Diaspora, Exile &amp; "Un-homing"</li>
							<li>Land, Struggle &amp; Reclamation</li>
							<li>Propaganda &amp; National Love</li>
							<li>Urban Imaginaries</li>
							<li>Sci-Fi &amp; Speculative World-Building</li>
							<li>Outer Space &amp; the Cosmic</li>
							<li>Ecologies &amp; Planetary Care</li>
							<li>Virtual &amp; Digital Spaces</li>
						</ul>
					</p>
				</Grid>
				<Grid container xs={12} className={styles.container}>
					<h3>
						Relevant Watch List
					</h3>
					<ul className={styles.listempty}>
						<li>1935 — <b>Triumph of the Will</b>, Leni Riefenstahl</li>
						<li>1953 — <b>Tokyo Story</b>, Yasujirō Ozu</li>
						<li>1954 — <b>Godzilla (Gojira)</b>, Ishirō Honda</li>
						<li>1959 — <b>Hiroshima mon amour</b>, Alain Resnais</li>
						<li>1964 — <b>Soy Cuba</b>, Mikhail Kalatozov</li>
						<li>1966 — <b>The Battle of Algiers</b>, Gillo Pontecorvo</li>
						<li>1973 — <b>Garam Hava</b>, M.S. Sathyu</li>
						<li>1977 — <b>News from Home</b>, Chantal Akerman</li>
						<li>1983 — <b>Sans Soleil</b>, Chris Marker</li>
						<li>1983 — <b>Nostalghia</b>, Andrei Tarkovsky</li>
						<li>1987 / 1992 / 1994 — <b>Koker Trilogy</b>, Abbas Kiarostami</li>
						<li>1991 — <b>Paris is Burning</b>, Jennie Livingston</li>
						<li>2007 — <b>Persepolis</b>, Marjane Satrapi &amp; Vincent Paronnaud</li>
						<li>2010 — <b>Uncle Boonmee Who Can Recall His Past Lives</b>, Apichatpong Weerasethakul</li>
						<li>2017 — <b>The Florida Project</b>, Sean Baker</li>
						<li>2018 — <b>Roma</b>, Alfonso Cuarón</li>
						<li>2022 — <b>All That Breathes</b>, Shaunak Sen</li>
						<li>2024 — <b>No Other Land</b>, Basel Adra, Hamdan Ballal, Yuval Abraham &amp; Rachel Szor</li>
					</ul>
				</Grid>
				<Grid container xs={12} className={styles.container}>
					<h3>
						Relevant Reading List
					</h3>
					<ul className={styles.listempty}>
						<li>Bachelard, Gaston — <b>The Poetics of Space</b>, 1964 (Translated by Maria Jolas, Beacon Press, 1994)</li>
						<li>Bhabha, Homi K — <b>The Location of Culture</b> (Routledge, 1994)</li>
						<li>Cosgrove, Denis — <b>Social Formation and Symbolic Landscape</b> (Croom Helm, 1984)</li>
						<li>Jameson, Fredric — <b>Archaeologies of the Future: The Desire Called Utopia and Other Science Fictions</b> (Verso, 2005)</li>
						<li>Kitchin, Rob, &amp; Kneale, James — <b>Lost in Space: Geographies of Science Fiction</b> (Continuum, 2002)</li>
						<li>Lukinbeal, Chris, &amp; Zimmermann, Stephan — <b>The Geography of Cinema</b> (Transcript Verlag, 2008)</li>
						<li>MacDonald, Scott — <b>The Garden in the Machine: A Field Guide to Independent Cinema</b> (University of California Press, 2001)</li>
						<li>Naficy, Hamid — <b>An Accented Cinema: Exilic and Diasporic Filmmaking</b> (Princeton University Press, 2001)</li>
						<li>Rose, Gillian — <b>Feminism and Geography: The Limits of Geographical Knowledge</b> (University of Minnesota Press, 1993)</li>
						<li>Said, Edward W — <b>Reflections on Exile and Other Essays</b> (Harvard University Press, 2000)</li>
						<li>Saunders, Robert A — <b>Popular Geopolitics and Nation Branding in the Post-Soviet Realm</b> (Routledge, 2017)</li>
						<li>Tuan, Yi-Fu — <b>Topophilia: A Study of Environmental Perception, Attitudes, and Values</b> (Columbia University Press, 1974)</li>
						<li>Gray, Ros &amp; Eshun, Kodwo (eds.) — <b>The Militant Image: A Ciné-Geography</b> (Cambridge University, 2020) 
						<br/> <a href="https://www.mmll.cam.ac.uk/sites/default/files/the_militant_image_editors_introduction_0.pd" className={styles.link} target="_blank" rel="noreferrer">'https://www.mmll.cam.ac.uk/sites/default/files/the_militant_image_editors_introduction_0.pd'</a> </li>
					</ul>
				</Grid>
				<Grid container xs={12} className={styles.container}>
					<h3>
						Submissions
					</h3>
					<ol className={`${styles.listmain}`}>
						<span className={`${styles.bold} ${styles.italic}`}>
							Chalchitra Darpan{' '}
						</span> accepts a variety of written pieces for submission, such as:
						<li>
							Essays for our 'Features' section, which should be between 3,000-5,000 words (including footnotes, excluding bibliography)
						</li>
						<li>
							Shorter articles of approximately 1,000-3,000 words (including footnotes, excluding bibliography)
						</li>
						<li>
							Book Reviews, which are essays that provide a scholarly critique of films. The text choice may range from the
							theoretical and the practical to the pedagogical and the historical. They are typically 1,000 words (including
							footnotes, excluding bibliography)
						</li>
						<li>Interview, with no more than 10 questions (Transcript must be included)</li>
						<li>Film Criticism and Reviews, between 1000 and 1500 words</li>
					</ol>
					<p className={styles.paragraph}>
						While this is largely an undergraduate journal, we do encourage expert comments or articles from researchers
						working in the field.
					</p>
					<p className={`${styles.bold} ${styles.paragraph}`}>
						<b>All submissions should not be under consideration elsewhere, and should be original and previously unpublished.</b>
					</p>
				</Grid>
				<Grid container xs={12} className={styles.container}>
					<h3>
						Abstract Submission
					</h3>
					<p className={styles.paragraph}>
						Proposal abstracts should be limited to 250 words and must be accompanied by an indicative bibliography. A brief
						biography of the author of approx. 150 words should be provided along with the abstract. Abstracts should be sent
						through as Word Documents and titled <span className={styles.bold}>"For consideration: Author First name Author
						Surname_Type of Submission"</span> (e.g. For consideration: Mary Poppins_Essay).
					</p>
					<p className={styles.paragraph}>
						<b>Please mail your proposal to
						<a href="mailto:chalchitradarpan@gmail.com" className={styles.link} target="_blank" rel="noreferrer"> chalchitradarpan@gmail.com</a></b>
					</p>
				</Grid>
				<Grid container xs={12} className={`${styles.container} ${styles.column}`}>
					<h3>
						Closed for Submissions
					</h3>
				</Grid>
			</div>
		</React.Fragment>
  )
}

export default CallForPapers2k25
